import React from 'react';
import Text from '../components/elements/TextComponent';
import Button from '../components/elements/Button';
import ImageComponent from '../components/elements/ImageComponent';
import userAtDesk from '../assets/userAtDesk.svg';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const EFFECTIVE_DATE = 'July 30, 2025';
const PRODUCT_NAME = 'ComeChatWithMe.ai';
const CONTACT_EMAIL = 'comechatwithmeai@gmail.com';
// Choose/adjust your jurisdiction if desired:
const GOVERNING_LAW = 'State of Israel';

interface Props {}

const TermsOfService: React.FC<Props> = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.98 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
    >
      <div className="split-screen">
        {/* Left side (unchanged) */}
        <div className="left-side color-purple-background">
          <div className="margin-top-20p" style={{ width: '50vw', height: '100vh' }}>
            <ImageComponent src={userAtDesk} center />
            <Text
              text={PRODUCT_NAME}
              fontSize="32px"
              fontWeight="700"
              lineHeight="38px"
              color="#ffffff"
              textAlign="center"
              navigateToHome
            />
            <div className="margin-top-30">
              <Text
                text="Create and share personalized assistants effortlessly."
                fontSize="18px"
                fontWeight="400"
                lineHeight="18px"
                color="#ffffff"
                textAlign="center"
              />
            </div>
          </div>
        </div>

        {/* Right side (terms content) */}
        <div className="right-side">
          <div className="margin-top-64 margin-right-64">
            <Text
              text={PRODUCT_NAME}
              fontSize="32px"
              fontWeight="700"
              lineHeight="38px"
              color="#030303"
              textAlign="right"
              navigateToHome
            />
            <Text
              text="Terms of Service"
              fontSize="16px"
              fontWeight="400"
              lineHeight="18px"
              color="#030303"
              textAlign="right"
            />
          </div>

          <div className="margin-top-64 margin-left-20p" style={{ maxWidth: 720 }}>
            <Text
              text="Terms of Service"
              fontSize="32px"
              fontWeight="700"
              lineHeight="38px"
              color="#030303"
              textAlign="left"
            />
            <div className="margin-top-10">
              <Text
                text={`Effective Date: ${EFFECTIVE_DATE}`}
                fontSize="14px"
                fontWeight="400"
                lineHeight="18px"
                color="#4a4a4a"
                textAlign="left"
              />
              <Text
                text={`Contact: ${CONTACT_EMAIL}`}
                fontSize="14px"
                fontWeight="400"
                lineHeight="18px"
                color="#4a4a4a"
                textAlign="left"
              />
            </div>

            <div
              className="margin-top-20 typer-scroll-container"
              style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 12 }}
            >
              <Section title="1. Agreement & Scope">
                {`${PRODUCT_NAME} provides tools for creating and sharing chatbots. By accessing or using the Platform, you agree to these Terms. If you do not agree, do not use the Platform.`}
              </Section>

              <Section title="2. Accounts & Eligibility">
                <>
                  <Bullet>Provide accurate information and keep your account secure.</Bullet>
                  <Bullet>You must be at least 13 years old (or the age of consent in your jurisdiction).</Bullet>
                </>
              </Section>

              <Section title="3. Subscriptions, Billing & Cancellations">
                <>
                  <Bullet>
                    Monthly subscription fees grant access to premium features. Payments are processed by third-party processors; we do not store card numbers.
                  </Bullet>
                  <Bullet>
                    Subscriptions auto-renew unless canceled before the renewal date. You can cancel at any time in your account settings (or by contacting support).
                  </Bullet>
                  <Bullet>
                    Refunds are handled in our discretion and where required by law.
                  </Bullet>
                </>
              </Section>

              <Section title="4. Visitor Conversations">
                <>
                  <Bullet>
                    Visitors/recruiters acknowledge that their chat messages and timestamps will be visible to the bot owner.
                  </Bullet>
                  <Bullet>
                    Bot owners are responsible for how they use the conversation data.
                  </Bullet>
                </>
              </Section>

              <Section title="5. Acceptable Use">
                <>
                  <Bullet>Do not upload or share illegal, harmful, or infringing content.</Bullet>
                  <Bullet>Do not harass, spam, or misuse the Platform.</Bullet>
                  <Bullet>Do not attempt to bypass security or disrupt service.</Bullet>
                </>
              </Section>

              <Section title="6. Intellectual Property">
                <>
                  <Bullet>
                    We retain all rights to the Platform. You retain ownership of your uploaded content, but grant us a limited license to host and process it to provide the service.
                  </Bullet>
                </>
              </Section>

              <Section title="7. Privacy">
                {`Your use of the Platform is subject to our Privacy Policy. Please review it for details on how we collect and use data.`}
              </Section>

              <Section title="8. Third-Party Services">
                {`We may rely on third-party providers (e.g., hosting, analytics, payment processors). We are not responsible for their services, but we take reasonable steps to work with reputable providers.`}
              </Section>

              <Section title="9. Disclaimers">
                {`The Platform is provided “as is” and “as available.” We make no warranties of fitness for a particular purpose, merchantability, or non-infringement to the maximum extent permitted by law.`}
              </Section>

              <Section title="10. Limitation of Liability">
                {`To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or exemplary damages. Our total liability for any claim is limited to the amount you paid in the 12 months preceding the event.`}
              </Section>

              <Section title="11. Indemnity">
                {`You agree to indemnify and hold us harmless from claims arising out of your content, your use of the Platform, or your violation of these Terms.`}
              </Section>

              <Section title="12. Termination">
                {`We may suspend or terminate your access if you violate these Terms, pose a risk, or as required by law. You may stop using the Platform at any time.`}
              </Section>

              <Section title="13. Changes to Terms">
                {`We may update these Terms from time to time. If changes are material, we will notify you via email or in-app notice. Continued use constitutes acceptance.`}
              </Section>

              <Section title="14. Governing Law">
                {`These Terms are governed by the laws of the ${GOVERNING_LAW}, without regard to conflict of law principles. Venue and jurisdiction will be in the competent courts of that jurisdiction.`}
              </Section>

              <Section title="15. Contact">
                {`Questions? Contact ${CONTACT_EMAIL}.`}
              </Section>
            </div>

            <div className="margin-top-20 margin-bottom-40" style={{ display: 'flex', gap: 12 }}>
              <Button
                backgroundColor="var(--primary)"
                color="#ffffff"
                label="Back to Login"
                onClick={() => navigate('/login')}
              />
              <Button
                backgroundColor="#eaeaea"
                color="#030303"
                label="Privacy Policy"
                onClick={() => navigate('/privacypolicy')}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TermsOfService;

/** ---------- Small helpers so we can keep your Text component everywhere ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="margin-top-20">
    <Text
      text={title}
      fontSize="18px"
      fontWeight="700"
      lineHeight="24px"
      color="#030303"
      textAlign="left"
    />
    <div className="margin-top-10">{children}</div>
  </div>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
    <Text text="•" fontSize="14px" fontWeight="700" lineHeight="22px" color="#030303" textAlign="left" />
    <Text
      text={String(children)}
      fontSize="14px"
      fontWeight="400"
      lineHeight="22px"
      color="#030303"
      textAlign="left"
    />
  </div>
);
